import type { components } from "@majitap/sdk";
import { ListView, useCursorList } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@majitap/ui/arc/popover/popover";
import { Link } from "@tanstack/react-router";
import { MessageSquareText } from "lucide-react";
import { type ReactNode, useState } from "react";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { deliveryHref } from "../../deliveries";
import { messageFor } from "../../error-copy";
import { DELIVERY_STATUS } from "../../labels";
import listStyles from "../orders/orders.module.css";
import { AssignDialog, PLANNABLE } from "./assign-dialog";
import styles from "./table.module.css";

type Row = components["schemas"]["DeliveryListItem"];
/** The list's filters Dispatch passes on: zone, driver, search and, for one day, its range. */
export interface TableFilters {
  zone?: string;
  driver?: string;
  q?: string;
  from?: string;
  to?: string;
  /** the tab's statuses, comma separated; none for All */
  status?: string;
}

/** "21 Oct, 09:30" in the company's zone; the year too when it isn't this year. */
function dateTime(iso: string, timezone: string) {
  const at = new Date(iso);
  const year = (d: Date) => new Intl.DateTimeFormat("en-GB", { timeZone: timezone, year: "numeric" }).format(d);
  const date = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    day: "numeric",
    month: "short",
    ...(year(at) === year(new Date()) ? {} : { year: "numeric" }),
  }).format(at);
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", minute: "2-digit" }).format(at);
  return { date, time };
}

/** The order's note: an icon that shows it on hover, and keeps it open on click (P8-76). */
function NoteHint({ note }: { note: string }): ReactNode {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  return (
    <Popover
      open={hover || pinned}
      onOpenChange={(open) => {
        setPinned(open);
        if (!open) setHover(false);
      }}
    >
      <PopoverTrigger
        className={styles.noteButton}
        aria-label="The order's note"
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
      >
        <MessageSquareText size={14} aria-hidden />
      </PopoverTrigger>
      <PopoverContent className={styles.note} onOpenAutoFocus={(e) => e.preventDefault()}>
        <strong>Order note</strong>
        <p>{note}</p>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Dispatch as a table (P8-76, `Console / Dispatch / Table · All time`): the tab's deliveries over
 * all time or one day, unscheduled first, then the latest scheduled. A page at a time.
 */
export function DispatchTable({ filters, filtered }: { filters: TableFilters; filtered: boolean }): ReactNode {
  const format = useCompanyFormat();
  const list = useCursorList({
    queryKey: ["dispatch-table", filters],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/dispatch/deliveries", {
        params: { query: { ...filters, sort: "latest", cursor, limit: 25 } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  const [assigning, setAssigning] = useState<Row | null>(null);

  return (
    <>
      <ListView<Row>
        caption="Deliveries"
        itemName={{ one: "delivery", other: "deliveries" }}
        rows={list.rows}
        rowKey={(d) => d.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={filtered}
        empty={{ title: "Nothing here", description: "Pick another tab to see the rest." }}
        columns={[
          {
            key: "delivery_number",
            label: "Delivery",
            // one still to plan opens the assign dialog (P8-77); the rest open their page
            render: (d) =>
              PLANNABLE.has(d.status) ? (
                <button
                  type="button"
                  className={`${listStyles.link} ${styles.numberButton}`}
                  onClick={() => setAssigning(d)}
                >
                  {d.delivery_number}
                </button>
              ) : (
                <Link to={deliveryHref(d.order.id, d.id)} className={listStyles.link}>
                  {d.delivery_number}
                </Link>
              ),
          },
          {
            key: "customer",
            label: "Customer",
            render: (d) => (
              <span className={listStyles.who}>
                <span>
                  {d.business?.name ?? d.customer.name}
                  {/* billed on account: not a payment to chase before it goes out */}
                  {d.order.on_credit ? (
                    <>
                      {" "}
                      <Badge tone="info">Credit</Badge>
                    </>
                  ) : null}
                </span>
                <span className={`${listStyles.muted} ${styles.place}`}>
                  {d.location?.label ?? "No place"}
                  {d.note ? <NoteHint note={d.note} /> : null}
                </span>
              </span>
            ),
          },
          { key: "zone", label: "Zone", render: (d) => d.zone?.name ?? <span className={listStyles.muted}>None</span> },
          {
            key: "fillsite",
            label: "Fill site",
            render: (d) => d.fillsite?.name ?? <span className={listStyles.muted}>Not chosen</span>,
          },
          {
            key: "driver",
            label: "Driver",
            render: (d) =>
              d.driver || d.truck ? (
                <span className={listStyles.who}>
                  <span>{d.driver?.name ?? "No driver"}</span>
                  <span className={listStyles.muted}>{d.truck?.plate_number ?? "No truck"}</span>
                </span>
              ) : (
                <span className={listStyles.muted}>Not assigned</span>
              ),
          },
          {
            key: "litres",
            label: "Water",
            numeric: true,
            render: (d) =>
              d.litres ? `${d.litres.toLocaleString("en-GB")} L` : <span className={listStyles.muted}>—</span>,
          },
          {
            key: "status",
            label: "Status",
            render: (d) => {
              const since = dateTime(d.status_since, format.timezone);
              const today = dateTime(new Date().toISOString(), format.timezone).date === since.date;
              return (
                <span className={listStyles.who}>
                  <span>
                    <Badge tone={DELIVERY_STATUS[d.status][1]}>{DELIVERY_STATUS[d.status][0]}</Badge>
                  </span>
                  <span className={listStyles.muted}>
                    {d.held_reason ?? `since ${today ? since.time : `${since.date}, ${since.time}`}`}
                  </span>
                </span>
              );
            },
          },
          {
            key: "scheduled_at",
            label: "Scheduled",
            render: (d) => {
              if (!d.scheduled_at) return <span className={listStyles.muted}>As soon as possible</span>;
              const at = dateTime(d.scheduled_at, format.timezone);
              return (
                <span className={listStyles.who}>
                  <span>{at.date}</span>
                  <span className={d.late ? listStyles.danger : listStyles.muted}>
                    {d.late ? `${at.time} · late` : at.time}
                  </span>
                </span>
              );
            },
          },
        ]}
      />
      {assigning ? <AssignDialog row={assigning} onClose={() => setAssigning(null)} /> : null}
    </>
  );
}
